import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import { createUseStyles } from '../../../../../../../../theming';
import { CircularProgress, Collapse, IconButton, InputBase, TextField } from '@material-ui/core';
import { Controller, useForm } from 'react-hook-form';
import { ImAttachment as AttachmentIcon } from '@react-icons/all-files/im/ImAttachment';
import { ImStopwatch as AddTimeIcon } from '@react-icons/all-files/im/ImStopwatch';
import Button from '../../../../../../../../components/Button';
import FileView from './components/FileView';
import TimeTrackingDialog from './components/TimeTrackingDialog';

const useStyles = createUseStyles({
  root: {
    marginTop: 12,
  },
  title: {
    width: 400,
    '& h2': {
      color: 'rgba(0, 0, 0, 0.87)',
    },
  },
  error: {
    color: '#f44336',
    fontSize: '.75rem',
    padding: '0 25px',
  },
  titleInput: {
    marginBottom: '8px !important',
  },
  commentInput: {
    padding: '12px !important',
  },
  commentContainer: {
    border: '1px solid #00000036',
    borderRadius: 4,
  },
  bottomContainer: {
    display: 'flex',
    padding: 12,
    justifyContent: 'space-between',
    alignItems: 'center',
    background: '#ececec',
  },
  filesContainer: {
    display: 'flex',
    paddingLeft: 6,
    paddingBottom: 6,
    paddingRight: 6,
    alignItems: 'center',
    flexWrap: 'wrap',
    '& div': {
      marginTop: 6,
      marginRight: 6,
    },
  },
});

interface Props {
  open: boolean;
  onClose: () => void;
  onAddPost: (title: string, comment: string, attachments?: File[]) => void;
}

export interface PostCommentOptions {
  title: string;
  comment: string;
  interventionTime?: number;
}

const CommentCard: React.FC<Props> = ({ open, onClose, onAddPost }) => {
  const [formError, setFormError] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);
  const inputFileRef = useRef<HTMLInputElement>(null);
  const [attachmentsFiles, setAttachmentsFiles] = useState<File[]>([]);
  const [timeTrackingDialogOpened, setTimeTrackingDialogOpened] = useState(false);

  const { reset, control, handleSubmit } = useForm<PostCommentOptions>({
    defaultValues: {
      comment: '',
      title: '',
    },
  });

  const classes = useStyles();

  const onSubmit = async (postCommentOptions: PostCommentOptions) => {
    const { title, comment } = postCommentOptions;

    try {
      setLoading(true);
      setFormError(undefined);
      await onAddPost(title, comment, attachmentsFiles);
    } catch (e) {
      setFormError(e.message);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!open) {
      setFormError(undefined);
      setAttachmentsFiles([]);
      reset({ title: '', comment: '' });
    }
  }, [reset, open]);

  const handleRemoveAttachment = (fileToRemove: File) => {
    const newFiles = attachmentsFiles.filter((file) => file.name !== fileToRemove.name);
    setAttachmentsFiles(newFiles);
  };

  const onPickImage = async (e: ChangeEvent<HTMLInputElement>) => {
    const targetFiles = e.target?.files || [];

    if (targetFiles.length > 0) {
      const file = targetFiles[0];
      setAttachmentsFiles((files) => [...files, file]);
    }
  };

  return (
    <>
      <Collapse in={open}>
        <div className={classes.root}>
          <Controller
            name='title'
            control={control}
            rules={{ required: { value: true, message: 'Le champ est requis' } }}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                className={classes.titleInput}
                variant='outlined'
                placeholder='Titre du commentaire'
                fullWidth
                error={undefined !== error}
                helperText={error?.message}
              />
            )}
          />

          <div className={classes.commentContainer}>
            <Controller
              name='comment'
              control={control}
              rules={{ required: { value: true, message: 'Le champ est requis' } }}
              render={({ field, fieldState: { error } }) => (
                <>
                  <InputBase
                    {...field}
                    className={classes.commentInput}
                    placeholder='Ecrivez un commentaire ...'
                    fullWidth
                    multiline
                    minRows={5}
                    error={undefined !== error}
                  />
                  {error?.message && <div className={classes.error}>{error?.message}</div>}
                </>
              )}
            />

            {attachmentsFiles.length > 0 && (
              <div className={classes.filesContainer}>
                {attachmentsFiles.map((file, key) => (
                  <FileView key={key} onDelete={() => handleRemoveAttachment(file)} file={file} />
                ))}
              </div>
            )}

            <div className={classes.bottomContainer}>
              <div>
                <IconButton onClick={() => inputFileRef.current?.click()}>
                  <AttachmentIcon size={15} />
                  <input style={{ display: 'none' }} ref={inputFileRef} type='file' onChange={onPickImage} />
                </IconButton>

                <IconButton onClick={() => setTimeTrackingDialogOpened(true)}>
                  <AddTimeIcon size={15} />
                </IconButton>
              </div>

              <Button
                type='submit'
                color='primary'
                variant='contained'
                disabled={loading}
                onClick={handleSubmit(onSubmit)}
              >
                {loading && <CircularProgress color='primary' size={15} />} Poster
              </Button>
            </div>
          </div>

          {formError && <p className={classes.error}>{formError}</p>}
        </div>
      </Collapse>

      <TimeTrackingDialog open={timeTrackingDialogOpened} onClose={() => setTimeTrackingDialogOpened(false)} />
    </>
  );
};

export default CommentCard;
